import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthState } from "@/auth/AuthProvider";
import { getBrowserTimeZone } from "@/lib/time";

export default function TimezoneSetting() {
  const { user } = useAuthState();
  const [tz, setTz] = useState(getBrowserTimeZone());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const zones = useMemo(() => {
    // Prefer native list if available; fall back to a short curated list.
    if (typeof Intl !== "undefined" && "supportedValuesOf" in Intl) {
      return (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf("timeZone");
    }
    return [
      "UTC",
      "America/Los_Angeles",
      "America/Denver",
      "America/Chicago",
      "America/New_York",
      "Pacific/Auckland",
      "Australia/Sydney",
      "Europe/London",
      "Europe/Paris",
    ];
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      const data = snap.data();
      setTz(data?.prefs?.timezone || getBrowserTimeZone());
      setLoading(false);
    })();
  }, [user]);

  async function save() {
    if (!user) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "users", user.uid), { prefs: { timezone: tz } }, { merge: true });
    } catch (err) {
      console.error("Failed to save timezone:", err);
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Time & Locale</h2>
        <p className="mt-1 text-sm text-slate-300">
          We use your browser timezone by default. You can override it here.
        </p>
      </div>
      <div className="space-y-2">
        <label htmlFor="timezone" className="block text-sm font-medium text-slate-200">
          Timezone
        </label>
        <select
          id="timezone"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          disabled={loading || saving}
          className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-white focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/50 disabled:bg-slate-800 disabled:text-slate-400"
        >
          {zones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={loading || saving}
          className="rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Timezone"}
        </button>
        <button
          onClick={() => setTz(getBrowserTimeZone())}
          disabled={loading || saving}
          className="text-sm text-brand-orange underline hover:text-brand-orange/80 disabled:opacity-50"
        >
          Use browser default
        </button>
      </div>
    </section>
  );
}
